import * as React from 'react';

import { useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { useLocalization } from '@shared-ui/localization-context';

import { usePropertyListing } from 'src/common/stores/contexts';

import { UitkCardLink } from 'uitk-react-cards';

import { productsTrackingSchema } from 'utils/uis-prime-micro-messages';
import { updateSlotsParamForMapTravelAds } from 'utils/infosite-url-utils';

import { DoubleLink } from 'components/common/media/double-link';
import { ListingComponentProps } from 'components/search/listing';
import { PropertyFields, Theme } from 'src/common/__generated__/api';
import { TargetableLink } from 'components/common/targetable-link';

export interface CardLinkProps extends ListingComponentProps {
  listingClickHandler: (property: PropertyFields.Fragment) => void;
}

export const CardLink: React.FunctionComponent<CardLinkProps> = ({ listingClickHandler, referralObject, multiItem, mapListingView }) => {
  const property = usePropertyListing();
  const track = useClickTracker();

  const { id, dataAttributes, name, offerSummary, pinnedDetails, propertyDetailsLink, sponsoredListing } = property;

  const brandedDealType = offerSummary?.messages?.find(message => message.theme === Theme.BRANDED_DEAL)?.type;
  const pinned = Boolean(pinnedDetails && pinnedDetails.heading);

  if (!propertyDetailsLink?.uri) {
    return null;
  }

  let url = sponsoredListing && multiItem?.multiItemContext ? propertyDetailsLink.uri.value : propertyDetailsLink.uri.relativePath;

  if (mapListingView && sponsoredListing) {
    url = updateSlotsParamForMapTravelAds(url);
  }

  const listingDataAttributes = {};
  if (dataAttributes) {
    listingDataAttributes[dataAttributes.key] = dataAttributes.value;
  }

  const onClick = () => {
    if (listingClickHandler) {
      listingClickHandler(property);
    }

    track(
      referralObject?.navigation?.rfrr || propertyDetailsLink?.clientSideAnalytics?.referrerId,
      propertyDetailsLink?.clientSideAnalytics?.linkName,
      true,
      productsTrackingSchema(parseInt(id, 10), pinned, Boolean(sponsoredListing), multiItem?.getPackageType(), brandedDealType)
    );
  };

  const doubleLinkProps = {
    name,
    className: 'listing__link',
    'data-stid': 'open-hotel-information',
    to: url,
    onClick,
    ...listingDataAttributes,
    isTravelAd: Boolean(sponsoredListing?.clickRedirectLinkoffTrackingUrl),
  };

  if (doubleLinkProps.isTravelAd) {
    return <CardLinkBase {...doubleLinkProps} />;
  }

  return (
    <TargetableLink>
      <CardLinkBase {...doubleLinkProps} />
    </TargetableLink>
  );
};
CardLink.displayName = 'CardLink';

const CardLinkBase = ({ name, isTravelAd, ...doubleLinkProps }) => {
  const l10n = useLocalization();

  const travelAdA11yLabel = l10n.formatText('hotels.search.listing.allyLabelTravelAd', [name]);
  const listingA11yLabel = l10n.formatText('hotels.search.listing.a11yLabel', [name]);
  const newWindowMessage = l10n.formatText('hotels.infosite.policies.missingIncorrectInfo.newWindowText');

  return (
    <UitkCardLink data-stid="listing_link">
      <DoubleLink {...doubleLinkProps}>{isTravelAd ? travelAdA11yLabel : `${listingA11yLabel}, ${newWindowMessage}`}</DoubleLink>
    </UitkCardLink>
  );
};

CardLinkBase.displayName = 'CardLinkBase';
